<!-- src/views/reviews/PublicUserReviewFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans text-left">
    
    <!-- Dynamic Public Header Banner (Synchronised with Client Feedback) -->
    <div class="bg-[#0A1630] py-8 px-6 sm:px-10 border-b-4 border-[#20FCF1] shadow-xl text-center">
      <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-4 text-left">
          <div class="bg-white p-2.5 rounded-2xl shadow-md">
            <img v-if="clientLogoUrl" :src="clientLogoUrl" class="h-10 max-w-[150px] object-contain" alt="Client Logo" />
            <img v-else src="/images/sentinel-safe-logo.png" class="h-10 w-auto object-contain" alt="Sentinel Safe Logo" />
          </div>
          <div>
            <h1 class="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
              {{ clientName ? `${clientName}: ` : '' }}Review Form for {{ targetUserName || 'Sentinel Operative' }}
            </h1>
            <p class="text-[#20FCF1] font-black uppercase text-[9px] tracking-[0.2em] mt-1">Sentinel Safe Quality Assurance Protocol</p>
          </div>
        </div>
        <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 text-right">
          <span class="text-[9px] font-black text-slate-300 uppercase tracking-widest block">Standard Audit Protocol</span>
        </div>
      </div>
    </div>

    <!-- MASTER CONTAINER (Synchronised with max-w-7xl alignment) -->
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col items-center py-20 bg-white rounded-[40px] shadow-xl border border-slate-200">
          <div class="animate-spin h-10 w-10 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
          <p class="text-[10px] font-black uppercase tracking-widest text-slate-400">Initialising Secure Portal...</p>
        </div>

        <!-- Success State -->
        <div v-else-if="submitted" class="bg-white p-8 sm:p-12 rounded-[40px] shadow-2xl border border-slate-200 text-center animate-fadeIn">
          <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-3xl bg-[#EBFDF8] border border-[#20FCF1]/30 shadow-lg mb-6">
            <CheckBadgeIcon class="h-12 w-12 text-[#20FCF1]" />
          </div>
          <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight mb-2">Review Verified</h2>
          <p class="text-sm font-bold text-slate-500 mb-10">Thank you. Your professional feedback has been securely logged for <strong>{{ targetUserName || 'the operative' }}</strong>.</p>
          
          <div class="pt-8 border-t border-slate-100 flex flex-col items-center">
            <p class="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4">Record Submitted To</p>
            <img v-if="clientLogoUrl" :src="clientLogoUrl" alt="Client Logo" class="h-12 mx-auto object-contain" />
            <span v-else class="text-lg font-black text-[#0A1630] uppercase tracking-tighter">{{ clientName || 'Sentinel Safe' }}</span>
          </div>
        </div>

        <!-- Main Form Container -->
        <div v-else class="bg-white p-6 sm:p-12 rounded-[40px] shadow-xl border border-slate-200">

          <form class="space-y-12 text-left" @submit.prevent="submitReview">
            
            <!-- Section 1: Management Details -->
            <section class="space-y-6">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">1. Reviewer Identification</h3>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div class="sm:col-span-2">
                  <label for="reviewerName" class="form-label">Site Manager Name <span class="text-red-500">*</span></label>
                  <input id="reviewerName" type="text" required v-model="form.reviewerName" class="form-input" placeholder="Your full name" />
                </div>
                <div>
                  <label for="company" class="form-label">Representing Company <span class="text-red-500">*</span></label>
                  <input id="company" type="text" required v-model="form.company" class="form-input" placeholder="Company name" />
                </div>
                <div>
                  <label for="siteName" class="form-label">Project / Site Name <span class="text-red-500">*</span></label>
                  <input id="siteName" type="text" required v-model="form.siteName" class="form-input" placeholder="Project name" />
                </div>
                <div class="sm:col-span-2">
                  <label for="email" class="form-label">Official Email Address <span class="text-red-500">*</span></label>
                  <input id="email" type="email" required v-model="form.email" class="form-input" placeholder="manager@company.co.uk" />
                </div>
              </div>
            </section>

            <!-- Section 2: Performance Metrics -->
            <section class="space-y-6">
              <div class="border-b border-slate-200 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">2. Core Competencies</h3>
                  <p class="text-[10px] text-slate-400 font-bold uppercase mt-1">Please rate each category (1 = Lowest, 5 = Highest).</p>
                </div>
                <p v-if="!isFormValid && !submitting" class="text-[9px] font-bold text-rose-500 uppercase tracking-widest animate-fadeIn">
                  All evaluation categories must be rated.
                </p>
              </div>

              <div class="space-y-4">
                <div v-for="metric in performanceOptions" :key="metric.id" 
                  class="bg-slate-50 p-6 rounded-3xl border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all hover:bg-slate-100"
                >
                  <span class="text-xs font-black uppercase tracking-tight text-[#0A1630]">{{ metric.label }}</span>
                  
                  <div class="flex items-center gap-2">
                    <button 
                      type="button"
                      v-for="star in 5" 
                      :key="star" 
                      @click="form.metrics[metric.id] = star"
                      class="w-10 h-10 rounded-xl font-black text-xs transition-all flex items-center justify-center border shadow-sm cursor-pointer" 
                      :class="star <= form.metrics[metric.id] ? 'bg-[#0A1630] text-[#20FCF1] border-[#0A1630]' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'"
                    >
                      {{ star }}
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Calculated Live Average -->
              <div v-if="currentAverage > 0" class="mt-6 flex items-center justify-between p-6 bg-[#0A1630] rounded-[32px] animate-fadeIn shadow-xl border-l-4 border-[#20FCF1]">
                <div class="flex flex-col">
                  <span class="text-[9px] font-black uppercase text-[#20FCF1] tracking-widest">Calculated Score</span>
                  <div class="flex mt-1">
                    <StarIcon v-for="s in 5" :key="s" class="h-3 w-3" :class="s <= Math.round(currentAverage) ? 'text-[#20FCF1]' : 'text-white/10'" />
                  </div>
                </div>
                <span class="text-3xl font-black text-white italic tracking-tighter">{{ currentAverage }}<span class="text-xs text-white/40 not-italic ml-1">/ 5.0</span></span>
              </div>
            </section>

            <!-- Section 3: Field Evidence -->
            <section class="space-y-6">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">3. Field Evidence & Remarks</h3>
              </div>

              <div>
                 <div class="p-8 bg-slate-50 border border-slate-200 rounded-[32px] flex flex-col items-center">
                    <input type="file" ref="fileInput" id="file-upload" accept="image/*" @change="handleImageUpload" class="hidden"/>
                    <button type="button" @click="triggerFileInput" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all cursor-pointer shadow-md">
                      {{ imagePreview ? 'Change Photo' : 'Capture Site Image' }}
                    </button>
                    
                    <div v-if="imagePreview" class="mt-6 animate-fadeIn">
                        <img :src="imagePreview" alt="Preview" class="h-32 w-32 object-cover rounded-2xl border-4 border-white shadow-xl" />
                    </div>
                 </div>
              </div>

              <div>
                <label for="comments" class="form-label">Additional Remarks</label>
                <textarea id="comments" rows="5" v-model="form.comments" class="form-input bg-slate-50" placeholder="Enter any specific feedback regarding the operative's conduct or work quality..."></textarea>
              </div>
            </section>

            <!-- Submit Action -->
            <div class="pt-6">
              <button type="submit" :disabled="submitting || !isFormValid" class="btn-premium">
                <span v-if="submitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Verifying Submission...
                </span>
                <span v-else>Submit Performance Review</span>
              </button>
            </div>
            
            <div v-if="errorMessage" class="rounded-[32px] bg-red-50 border border-red-100 p-6 flex items-center gap-4">
              <ExclamationTriangleIcon class="h-6 w-6 text-red-500" />
              <p class="text-xs font-black uppercase text-red-800 tracking-tight">{{ errorMessage }}</p>
            </div>

          </form>
        </div>
      </div>
    </div>

    <!-- 🛡️ MASTER CORPORATE FOOTER -->
    <footer class="bg-white border-t border-slate-200 py-12 mt-20 no-print">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 text-center space-y-4">
        <div class="flex items-center justify-center gap-3">
          <img src="/images/sentinel-safe-logo.png" class="h-6 w-auto object-contain" alt="Sentinel Safe Logo" />
          <span class="font-sans font-black text-xs uppercase tracking-[0.2em] text-[#0A1630]">Sentinel Safe</span>
        </div>
        <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
          Copyright &copy; 2026 Sentinel Safe. All Rights Reserved.
        </p>
        <p class="text-[8px] font-bold text-slate-400 uppercase tracking-wider">
          Sentinel Safe is operated and managed by Get Pixel Perfection Limited.<br>
          Get Pixel Perfection Limited is a company registered in England and Wales. Company Number: 16854093.<br>
          UK GDPR Compliant • Privacy Policy
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { 
  StarIcon, 
  CheckBadgeIcon, 
  ExclamationTriangleIcon
} from '@heroicons/vue/24/solid';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebaseConfig';

import { getFirestore, doc, getDoc } from 'firebase/firestore';

const route = useRoute();
const resolvedUserId = ref('');
const fileInput = ref(null);

const loading = ref(true);
const submitting = ref(false);
const submitted = ref(false);
const errorMessage = ref('');
const clientLogoUrl = ref('');
const clientName = ref('');
const targetUserName = ref('');
const imagePreview = ref('');

const form = reactive({
  reviewerName: '',
  email: '',
  company: '',
  siteName: '',
  metrics: {
    safety: 0,
    ppe: 0,
    comms: 0,
    punctual: 0,
    teamwork: 0,
    leadership: 0
  },
  comments: '',
  imageBase64: null
});

const performanceOptions = [
  { id: 'safety', label: 'Safety Conscious' },
  { id: 'ppe', label: 'PPE Compliance' },
  { id: 'comms', label: 'Communication' },
  { id: 'punctual', label: 'Punctuality' },
  { id: 'teamwork', label: 'Team Player' },
  { id: 'leadership', label: 'Leadership' }
];

const currentAverage = computed(() => {
    const values = Object.values(form.metrics);
    const rated = values.filter(v => v > 0);
    if (rated.length === 0) return 0;
    const sum = rated.reduce((a, b) => a + b, 0);
    return (sum / rated.length).toFixed(1);
});

const isFormValid = computed(() => {
    return Object.values(form.metrics).every(v => v > 0) &&
           form.reviewerName.trim() !== '' &&
           form.email.trim() !== '' &&
           form.company.trim() !== '' &&
           form.siteName.trim() !== '';
});

const triggerFileInput = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            const MAX_WIDTH = 1024;
            const MAX_HEIGHT = 1024;

            if (width > height) {
                if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                }
            } else {
                if (height > MAX_HEIGHT) {
                    width *= MAX_HEIGHT / height;
                    height = MAX_HEIGHT;
                }
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                imagePreview.value = compressedDataUrl;
                form.imageBase64 = compressedDataUrl.split(',')[1];
            }
        };
        img.src = event.target.result;
    };
    reader.readAsDataURL(file);
};

const extractUserId = () => {
    let uid = route.params.userId || route.params.id;
    if (uid) return String(uid).trim();

    uid = route.query.userId || route.query.id;
    if (uid) return String(uid).trim();

    try {
        const fullUrlString = window.location.href;
        const urlSegments = fullUrlString.split(/[/?#]+/).filter(Boolean);
        
        for (const segment of urlSegments) {
            if (segment && segment.length === 28) {
                return segment.trim();
            }
        }
        
        for (const segment of urlSegments) {
            if (segment && segment.length >= 20 && segment.length <= 50) {
                const lowerSegment = segment.toLowerCase();
                if (!['public', 'review', 'reviews', 'user'].includes(lowerSegment)) {
                    return segment.trim();
                }
            }
        }
    } catch (e) {
        console.warn("Direct path scanning bypassed:", e);
    }

    return '';
};

const fetchClientInfo = async () => {
    const targetId = resolvedUserId.value;

    const lookupValue = (obj, targetKeys) => {
        if (!obj || typeof obj !== 'object') return '';
        for (const k of targetKeys) {
            if (obj[k] && typeof obj[k] !== 'object') return String(obj[k]);
        }
        const nestedWrappers = ['user', 'profile', 'data', 'target', 'operative'];
        for (const w of nestedWrappers) {
            if (obj[w] && typeof obj[w] === 'object') {
                for (const k of targetKeys) {
                    if (obj[w][k] && typeof obj[w][k] !== 'object') return String(obj[w][k]);
                }
            }
        }
        return '';
    };

    try {
        const getInfo = httpsCallable(functions, 'getPublicReviewTargetInfo');
        const result = await getInfo({ 
            userId: targetId,
            targetUserId: targetId,
            id: targetId 
        });

        if (result.data) {
            clientName.value = lookupValue(result.data, ['clientName', 'companyName', 'company', 'client']) || '';
            clientLogoUrl.value = lookupValue(result.data, ['clientLogoUrl', 'logoUrl', 'clientLogo', 'logo']);

            let resolvedName = '';
            const fName = lookupValue(result.data, ['firstName']);
            const lName = lookupValue(result.data, ['lastName']);
            if (fName || lName) {
                resolvedName = `${fName} ${lName}`.trim();
            }
            
            if (!resolvedName) {
                resolvedName = lookupValue(result.data, ['targetUserName', 'userName', 'displayName', 'fullName', 'name']);
            }

            targetUserName.value = resolvedName || '';
        }
    } catch (err) {
        console.error("Registry Data Fetch Error from Cloud Function:", err);
    }

    if (!targetUserName.value) {
        try {
            const db = getFirestore();
            const userDocRef = doc(db, 'userProfiles', targetId);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                const fName = userData.firstName || '';
                const lName = userData.lastName || '';
                if (fName || lName) {
                    targetUserName.value = `${fName} ${lName}`.trim();
                } else {
                    targetUserName.value = userData.userName || userData.displayName || userData.fullName || userData.name || '';
                }

                if (!clientName.value) {
                    clientName.value = userData.clientName || userData.companyName || userData.company || '';
                }
                if (!clientLogoUrl.value) {
                    clientLogoUrl.value = userData.clientLogoUrl || userData.logoUrl || '';
                }
            }
        } catch (firestoreErr) {
            console.error("Direct Firestore read warning:", firestoreErr);
        }
    }

    loading.value = false;
};

const submitReview = async () => {
  if (!isFormValid.value) return;
  submitting.value = true;
  errorMessage.value = '';

  try {
    const submitFunction = httpsCallable(functions, 'submitGenericForm');
    const payload = {
      formType: 'user_review', 
      targetUserId: resolvedUserId.value,    
      publicSubmission: true,  
      data: {
        reviewerName: form.reviewerName,
        email: form.email,
        company: form.company,
        siteName: form.siteName,
        rating: parseFloat(currentAverage.value),
        metricBreakdown: form.metrics,
        comments: form.comments,
        imageBase64: form.imageBase64,
        submittedAt: new Date().toISOString()
      }
    };

    const result = await submitFunction(payload);
    if (result.data && result.data.success) {
      submitted.value = true;
    } else {
      throw new Error(result.data ? result.data.message : 'Response empty');
    }
  } catch (error) {
    console.error("Registry Review Transmission Failure Details:", error);
    errorMessage.value = 'Failed to submit review. Please try again.';
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
    resolvedUserId.value = extractUserId();
    fetchClientInfo();
});
</script>

<style scoped>
.form-input { 
  @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm; 
}
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center cursor-pointer; 
}
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>